import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { CurrentUser, Role } from '@techsofit/api-types';
import { api, ApiClientError } from './api';

/**
 * Cookie-session auth shared by every portal, on the single unified ACCOUNT model.
 * One email is one account with many profiles (individual / gym staff / platform);
 * the active profile maps to the {@link CurrentUser} each app already reads, and
 * each app gates by role. Sign-in itself lives on the `/account-auth/*` endpoints
 * (passwordless code for gyms & individuals, password for platform admins) and is
 * driven by each app's own login screen. This context only resolves and tracks the
 * current user and handles logout. There is no separate legacy `/auth/*` path.
 */
export class WrongPortalError extends Error {
  constructor(message = 'Your account does not have access to this app.') {
    super(message);
    this.name = 'WrongPortalError';
  }
}

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  user: CurrentUser | null;
  status: Status;
  logout: () => Promise<void>;
}

interface InternalState extends AuthState {
  loginPath: string;
}

const AuthContext = createContext<InternalState | null>(null);

export interface AuthProviderProps {
  /** Roles permitted in this app; any other context resolves to anonymous here. */
  allowedRoles: Role[];
  /** Where {@link RequireAuth} sends anonymous users. */
  loginPath?: string;
  children: ReactNode;
}

export function AuthProvider({ allowedRoles, loginPath = '/login', children }: AuthProviderProps) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  const allowed = (u: CurrentUser) => allowedRoles.includes(u.role);

  useEffect(() => {
    let active = true;
    bootstrap(allowed).then((u) => {
      if (!active) return;
      setUser(u);
      setStatus(u ? 'authenticated' : 'anonymous');
    });
    return () => {
      active = false;
    };
    // allowedRoles is static per app; intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function logout() {
    await clearSession();
    setUser(null);
    setStatus('anonymous');
  }

  return (
    <AuthContext.Provider value={{ user, status, logout, loginPath }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Best-effort server-side revocation of the account session. */
async function clearSession() {
  await api<void>('/account-auth/logout', { method: 'POST' }).catch(() => undefined);
}

// ── Unified account session ───────────────────────────────────────────────────
// We resolve the current user from the account session and map the *active context*
// to the CurrentUser shape the apps already read, so no downstream code changes.

interface AccountProfile {
  id: string;
  orgId: string | null;
  orgName: string | null;
  role: string; // INDIVIDUAL | PLATFORM | MEMBER | OWNER | STAFF
}
interface AccountMe {
  accountId: string;
  email: string;
  fullName: string;
  activeProfileId: string;
  profiles: AccountProfile[];
  support?: { gymId: string; gymName: string } | null;
}

/** Map an account's active context to a CurrentUser, or null if it isn't an app role. */
function accountToCurrentUser(a: AccountMe): CurrentUser | null {
  const active = a.profiles.find((p) => p.id === a.activeProfileId);
  if (!active) return null;
  const role: Role | null =
    active.role === 'OWNER'
      ? 'GYM_OWNER'
      : active.role === 'STAFF'
        ? 'GYM_STAFF'
        : active.role === 'PLATFORM'
          ? 'PLATFORM_ADMIN'
          : null; // INDIVIDUAL / MEMBER have no staff/admin identity here
  if (!role) return null;
  return {
    id: a.accountId,
    fullName: a.fullName,
    email: a.email,
    role,
    gymId: active.orgId,
    gymName: active.orgName,
    support: a.support ?? null,
  };
}

async function bootstrap(allowed: (u: CurrentUser) => boolean): Promise<CurrentUser | null> {
  const user = await resolveUser();
  if (user && !allowed(user)) {
    // A session for another portal is open — sign it out of this app.
    await clearSession();
    return null;
  }
  return user;
}

async function resolveUser(): Promise<CurrentUser | null> {
  // A 200 with a non-app context (individual/member) maps to null → anonymous for
  // this app, which is correct (no gym/console to enter here).
  try {
    return accountToCurrentUser(await api<AccountMe>('/account-auth/me'));
  } catch {
    return null;
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/**
 * Gates the authenticated app. While the session resolves we render
 * {@code fallback}; once resolved, anonymous users are bounced to the login
 * path configured on the provider.
 */
export function RequireAuth({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('RequireAuth must be used within an AuthProvider');

  if (ctx.status === 'loading') return <>{fallback}</>;
  if (ctx.status === 'anonymous') return <Navigate to={ctx.loginPath} replace />;
  return <>{children}</>;
}

/** Maps a sign-in failure to friendly, portal-aware copy for the form. */
export function loginErrorMessage(err: unknown): string {
  if (err instanceof WrongPortalError) return err.message;
  if (err instanceof ApiClientError) {
    if (err.status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (err.status === 401) return 'Invalid email or password.';
    if (err.status === 400 && err.message) return err.message;
  }
  return 'Something went wrong. Please try again.';
}
